import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';
import type { PixelEvent } from '@/events';
import type { ConversionEventData } from '@/events/conversion';

import type { ConversionEventType } from './contracts';
import { AutoTrackerFactory } from './auto/AutoTrackerFactory';
import type { CommerceState, ProductCatalog } from './commerce';
import { ConversionDeduper } from './dedupe/ConversionDeduper';
import type { IConversionDetector } from './contracts';
import { TrackerFactory } from './tracks/TrackerFactory';

export class ConversionEngine {
  private readonly context: PixelRuntimeContext;

  private readonly deduper = new ConversionDeduper();

  private readonly dedupeSessionId: string;

  /** Catálogo da instância — via CommerceService (root). */
  readonly catalog: ProductCatalog;

  /**
   * Estado de cart da instância (CommerceService).
   * Escrito de forma controlada no TrackerFactory após ADD/REMOVE.
   */
  readonly commerceState: CommerceState;

  readonly factory: TrackerFactory;

  private detectors: IConversionDetector[] = [];

  private active = false;

  constructor(context: PixelRuntimeContext) {
    this.context = context;
    this.dedupeSessionId = `${context.config.identity.pixelId || context.config.identity.workspaceId || 'pixel'}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;

    const commerce = context.services.commerce;
    this.catalog = commerce.catalog;
    this.commerceState = commerce.state;

    this.factory = new TrackerFactory(
      context,
      (type, data, options) => {
        this.dispatch(type, data, options?.dedupeKey);
      },
      this.dedupeSessionId,
      { catalog: this.catalog, state: this.commerceState }
    );
  }

  init(): void {
    if (this.active) return;

    this.detectors = AutoTrackerFactory.create(this.context, this.factory);
    for (const detector of this.detectors) {
      detector.init();
    }

    this.active = true;
  }

  destroy(): void {
    if (!this.active) return;

    for (let i = this.detectors.length - 1; i >= 0; i -= 1) {
      this.detectors[i]?.destroy();
    }
    this.detectors = [];
    this.deduper.clear();
    // CommerceState vive no CommerceService (runtime) — não limpar aqui.
    this.active = false;
  }

  getDedupeKeys(): readonly string[] {
    return this.deduper.getKeys();
  }

  getActiveDetectors(): readonly string[] {
    return this.detectors.map(
      detector => detector.name ?? detector.constructor.name
    );
  }

  isActive(): boolean {
    return this.active;
  }

  private dispatch(
    type: ConversionEventType,
    data: ConversionEventData,
    dedupeKey?: string
  ): void {
    const key = dedupeKey ?? this.factory.buildDedupeKey(type, data, 'manual');

    if (this.deduper.has(key)) {
      this.context.logger.debug(`[ConversionEngine] Deduped ${type} (${key})`);
      return;
    }

    this.deduper.add(key);

    const event = this.context.services.eventBuilder.build({
      type,
      category: 'CONVERSION' as const,
      data,
    }) as PixelEvent;

    this.context.logger.debug(`[ConversionEngine] Dispatching ${type}`);
    this.context.services.dispatcher.dispatch(event);
  }
}
